import useAssemblies, { PAGE_SIZE } from './useAssemblies';
import { ReadOnlyGuard } from 'components/ReadOnlyGuard';
import { Link } from 'react-router-dom';
import PropertyCombobox from 'components/PropertyCombobox';
import AssemblyReport from 'components/AssemblyReport';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';
import ConfirmarFinalizarModal from 'components/ConfirmarFinalizarModal';
import ConfirmarReiniciarModal from 'components/ConfirmarReiniciarModal';
import OrdenDiaButton from './OrdenDiaButton';
import AnexosButton from './AnexosButton';
import EleccionesButton from './EleccionesButton';
import Paginacion from 'components/Paginacion';
import styles from './Assemblies.module.css';

const STATUS_MAP = {
  pendiente:   { label: 'Pendiente',   badge: 'badge-warning',   btn: 'btn-success',   icon: 'fa-play',    next: 'Iniciar' },
  en_progreso: { label: 'En progreso', badge: 'badge-success',   btn: 'btn-danger',    icon: 'fa-stop',    next: 'Finalizar' },
  finalizada:  { label: 'Finalizada',  badge: 'badge-secondary', btn: 'btn-secondary', icon: 'fa-history', next: 'Reiniciar' },
};

// Lista plana: con `estadoDe` (varias propiedades) la membresía se evalúa por fila y se
// muestra junto al conjunto; sin él, isReadOnly/isBlocked valen para toda la tabla.
function AssemblyTable({ assemblies, onStatusChange, onDelete, mostrarConjunto, estadoDe, isReadOnly, isBlocked, onJitsi, onOrdenSaved }) {
  return (
    <div>
      {!estadoDe && isBlocked && (
        <div className={styles.bloqueoTabla}>
          <i className={`fas fa-ban ${styles.iconoAviso}`} />
          <span><strong>Membresía bloqueada.</strong> No puedes gestionar asambleas de esta propiedad hasta renovar.</span>
        </div>
      )}
      {!estadoDe && isReadOnly && !isBlocked && (
        <div className={styles.lecturaTabla}>
          <i className={`fas fa-lock ${styles.iconoAviso}`} />
          <span><strong>Solo lectura.</strong> La membresía venció: puedes consultar la información durante el período de gracia, pero no crear, iniciar ni modificar asambleas. Una asamblea que ya estaba <strong>en curso</strong> sí se puede terminar.</span>
        </div>
      )}
      <div className="table-wrapper">
        {/* Por debajo de 1400 px la misma <table> se ve como tarjetas (R114/R115): la tabla mide 1.078 px y trae 7 acciones por fila. Los roles ARIA se declaran
            porque display:block les quita la semántica nativa (mismo patrón que Asistentes). */}
        <table className={styles.tablaTarjetas} role="table">
          <thead>
            <tr role="row">
              <th role="columnheader">ID</th>
              {mostrarConjunto && <th role="columnheader">Conjunto</th>}
              <th role="columnheader">Fecha</th>
              <th role="columnheader">Quórum</th>
              <th role="columnheader">Tipo</th>
              <th role="columnheader">Modalidad</th>
              <th role="columnheader">Estado</th>
              {(estadoDe || !isBlocked) && <th role="columnheader">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {assemblies.map(a => {
              const st = STATUS_MAP[a.status] || STATUS_MAP.pendiente;
              const { readOnly: filaSoloLectura, blocked: filaBloqueada } = estadoDe ? estadoDe(a) : { readOnly: isReadOnly, blocked: isBlocked };
              // P1: en período de gracia solo se consulta; la asamblea que ya
              // estaba en curso se puede terminar (finalizar, reunión, elecciones).
              const operaFila = !filaSoloLectura || a.status === 'en_progreso';
              return (
                <tr key={a.id} role="row" className={styles.fila}>
                  <td role="cell" data-label="ID" className={styles.celdaId}>#{a.id}</td>
                  {mostrarConjunto && (
                    <td role="cell" data-label="Conjunto">
                      <strong>{a.property?.name ?? '—'}</strong>
                      {filaBloqueada && <span className={`badge badge-secondary ${styles.etiquetaFila}`}>BLOQUEADA</span>}
                      {filaSoloLectura && !filaBloqueada && <span className={`badge badge-warning ${styles.etiquetaFila}`}>SOLO LECTURA</span>}
                    </td>
                  )}
                  <td role="cell" data-label="Fecha">{a.date ? new Date(a.date).toLocaleDateString('es-CO') : '—'}</td>
                  <td role="cell" data-label="Quórum">{a.quorum}%</td>
                  <td role="cell" data-label="Tipo"><span className="badge badge-primary">{a.assembly_type}</span></td>
                  <td role="cell" data-label="Modalidad"><span className="badge badge-secondary">{a.assembly_model}</span></td>
                  <td role="cell" data-label="Estado"><span className={`badge ${st.badge}`}>{st.label}</span></td>
                  {(estadoDe || !filaBloqueada) && (
                    <td role="cell" data-label="Acciones" className={styles.celdaAcciones}>
                      {!filaBloqueada && <div className={`actions ${styles.accionesFila}`}>
                        {operaFila && (
                          <button className={`btn btn-sm ${st.btn} ${styles.botonPrincipal}`} onClick={() => onStatusChange(a.id)} title={st.next}>
                            <i className={`fas ${st.icon}`} /> {st.next}
                          </button>
                        )}
                        {['virtual','hybrid'].includes(a.assembly_model) && a.zoom_meeting_number && a.status === 'en_progreso' && (
                          <button className={`btn btn-sm btn-secondary btn-icon ${styles.botonVideo}`}
                            onClick={() => onJitsi(a)}
                            title="Unirse a reunión virtual" aria-label="Unirse a reunión virtual">
                            <i className="fas fa-video" />
                          </button>
                        )}
                        {/* Finalizada: sus datos son los del acta (el servidor rechaza editarlos). */}
                        {!filaSoloLectura && a.status !== 'finalizada' && <OrdenDiaButton assembly={a} onSaved={onOrdenSaved} />}
                        <AnexosButton assembly={a} soloLectura={filaSoloLectura} />
                        {operaFila && <EleccionesButton assembly={a} />}
                        {!filaSoloLectura && a.status !== 'finalizada' && (
                          <Link to={`/admin/asambleas/${a.id}/edit`} className="btn btn-sm btn-secondary btn-icon" title="Editar" aria-label="Editar"><i className="fas fa-pencil" /></Link>
                        )}
                        <Link to={`/results/${a.id}`} className="btn btn-sm btn-secondary btn-icon" title="Resultados" aria-label="Resultados" target="_blank" rel="noopener noreferrer"><i className="fas fa-chart-bar" /></Link>
                        {a.status === 'finalizada' && !filaBloqueada && (
                          <AssemblyReport assemblyId={a.id} compact />
                        )}
                        {!filaSoloLectura && (
                          <button className={`btn btn-sm btn-danger btn-icon ${styles.botonEliminar}`} onClick={() => onDelete(a)} title="Eliminar" aria-label="Eliminar"><i className="fas fa-trash" /></button>
                        )}
                      </div>}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Assemblies() {
  const {
    properties, filterProp, selectedPropObj, loading, search, setSearch, page, setPage,
    total, alert, avisoTodas, confirmDel, setConfirmDel, deleting, delError, setDelError, finalizar,
    setFinalizar, finalizando, setFinalizando, reiniciar, setReiniciar, reiniciando, setReiniciando, isSuperAdmin, isAdminMultiProp, isReadOnly, isBlocked,
    abrirReunionStaff, handleDelete, aplicarEstado, handleStatusChange, handleOrdenSaved,
    handleFilterProp, visibleAssemblies, irAPagina, variasPropiedades, estadoDe,
  } = useAssemblies();

  return (
    <div>
      <div className="page-header">
        <div>
          <h1><i className={`fas fa-gavel ${styles.iconoCabecera}`} />Asambleas</h1>
          <p>
            {isSuperAdmin
              ? 'Todas las asambleas, con su conjunto en la primera columna'
              : properties.length > 1
                ? filterProp
                  ? `Asambleas de ${selectedPropObj?.name || 'la propiedad seleccionada'}`
                  : 'Filtra por propiedad o ve todas tus asambleas en una sola lista'
                : 'Asambleas de tu propiedad'}
          </p>
        </div>
        <div className={styles.acciones}>
          {/* Selector de propiedad — admin con múltiples propiedades */}
          {(isSuperAdmin || (isAdminMultiProp && properties.length > 1)) && (
            <div className={styles.selectorPropiedad}>
              <i className={`fas fa-building ${styles.iconoSelector}`} />
              <PropertyCombobox value={filterProp} onChange={handleFilterProp} placeholder="— Todas las propiedades —" />
              {filterProp && (
                <button className="btn btn-sm btn-secondary btn-icon"
                  onClick={() => handleFilterProp('')} title="Limpiar" aria-label="Limpiar filtro">
                  <i className="fas fa-times" />
                </button>
              )}
            </div>
          )}

          {/* Botón nueva asamblea — deshabilitado si bloqueado */}
          {!isBlocked && (
            <ReadOnlyGuard>
              <Link
                to={filterProp
                  ? `/admin/asambleas/create?property_id=${filterProp}`
                  : '/admin/asambleas/create'}
                className={`btn btn-primary ${isReadOnly ? styles.deshabilitado : ''}`}
              >
                <i className="fas fa-plus" /> Nueva asamblea
              </Link>
            </ReadOnlyGuard>
          )}
          {isBlocked && filterProp && (
            <button className="btn btn-primary" disabled title="Membresía bloqueada">
              <i className="fas fa-ban" /> Membresía bloqueada
            </button>
          )}
        </div>
      </div>

      {alert && <div className={`alert alert-${alert.type}`}>{alert.message}</div>}
      {avisoTodas && <div className="alert alert-warning" role="status">{avisoTodas}</div>}

      {/* Banner de estado membresía cuando hay propiedad seleccionada */}
      {isBlocked && filterProp && (
        <div className={styles.bloqueoPagina}>
          <i className={`fas fa-ban ${styles.iconoAviso}`} />
          <span>
            <strong>Membresía bloqueada.</strong> La membresía de <strong>{selectedPropObj?.name}</strong> ha
            vencido y el período de gracia expiró. Contacta al super administrador para renovar.
          </span>
        </div>
      )}
      {isReadOnly && !isBlocked && filterProp && (
        <div className={styles.lecturaPagina}>
          <i className={`fas fa-lock ${styles.iconoAviso}`} />
          <span>
            <strong>Solo lectura.</strong> La membresía de <strong>{selectedPropObj?.name}</strong> está
            en período de gracia. Puedes ver las asambleas pero no crear, iniciar ni modificarlas; la que ya estaba en curso sí se puede terminar.
          </span>
        </div>
      )}

      {/* Búsqueda */}
      <div className={`card ${styles.tarjetaBusqueda}`}>
        <div className={styles.busquedaRelleno}>
          <div className={styles.buscadorCaja}>
            <i className={`fas fa-search ${styles.iconoBuscar}`} />
            <input className={`form-control ${styles.buscador}`} placeholder="Buscar por conjunto o tipo..."
              value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          </div>
        </div>
      </div>

      {/* Misma paginación arriba de la lista, para no tener que bajar toda la página */}
      {!loading && <Paginacion pagina={page} total={total} porPagina={PAGE_SIZE} onCambiar={irAPagina} arriba />}

      {loading ? (
        <div className="loading"><div className="spinner" /><p>Cargando...</p></div>
      ) : visibleAssemblies.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <i className="fas fa-gavel" />
            <p>{filterProp ? `No hay asambleas para ${selectedPropObj?.name}` : 'No hay asambleas registradas'}</p>
          </div>
        </div>
      ) : (
        // ── Lista plana; con varias propiedades lleva la columna «Conjunto» ──
        <div className="card">
          <div className="card-header">
            <h2><i className={`fas fa-list ${styles.iconoLista}`} />
              {filterProp ? selectedPropObj?.name : (variasPropiedades ? 'Todas las asambleas' : 'Lista de asambleas')}
            </h2>
            <span className="badge badge-primary">{total}</span>
          </div>
          <AssemblyTable
            assemblies={visibleAssemblies}
            onStatusChange={handleStatusChange}
            onDelete={setConfirmDel}
            mostrarConjunto={variasPropiedades}
            estadoDe={variasPropiedades ? estadoDe : undefined}
            isReadOnly={isReadOnly}
            isBlocked={isBlocked}
            onJitsi={abrirReunionStaff}
            onOrdenSaved={handleOrdenSaved}
          />
        </div>
      )}
      {/* Paginación del servidor: mismo pie que Propietarios y Administradores */}
      {!loading && <Paginacion pagina={page} total={total} porPagina={PAGE_SIZE} onCambiar={irAPagina} />}
      {finalizar && (
        <ConfirmarFinalizarModal abiertas={finalizar.abiertas} asamblea={finalizar.asamblea} loading={finalizando}
          onClose={() => setFinalizar(null)}
          onConfirm={async () => { setFinalizando(true); await aplicarEstado(finalizar.id); setFinalizando(false); setFinalizar(null); }} />
      )}
      {reiniciar && (
        <ConfirmarReiniciarModal asamblea={reiniciar.asamblea} loading={reiniciando}
          onClose={() => setReiniciar(null)}
          onConfirm={async () => { setReiniciando(true); await aplicarEstado(reiniciar.id); setReiniciando(false); setReiniciar(null); }} />
      )}
      {confirmDel && (
        <ConfirmDeleteModal title="Eliminar asamblea" onClose={() => { setConfirmDel(null); setDelError(''); }} onConfirm={handleDelete} deleting={deleting} error={delError}>
          <div className={styles.confirmaTitulo}>
            ¿Eliminar la asamblea <span className={styles.confirmaNumero}>#{confirmDel.number_assembly ?? confirmDel.id}</span>
            {confirmDel.property?.name ? <> de <span className={styles.confirmaNumero}>{confirmDel.property.name}</span></> : null}?
          </div>
        </ConfirmDeleteModal>
      )}
    </div>
  );
}